import type { FHIRModel } from '@ctw/shared/api/fhir/models/fhir-model';
import { FeedbackContext } from '@ctw/shared/content/patient-record-search-poc/feedback-provider';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { getApiServiceBaseUrl } from '@ctw/shared/utils/environments';
import type { Resource } from 'fhir/r4';
import { useContext, useMemo, useState } from 'react';

interface FeedbackForm {
  feedback: string;
  feedbackType: string;
}

interface SubmitFeedback {
  submit: (data: FeedbackForm) => void;
  isSubmitting: boolean;
  isSubmitted: boolean;
  isError: boolean;
  errorMessage?: string;
}

interface UseSubmitFeedbackOptions<TResource extends FHIRModel<Resource>> {
  resource: TResource;
}

export const useSubmitFeedback = <TResource extends FHIRModel<Resource>>({
  resource,
}: UseSubmitFeedbackOptions<TResource>): SubmitFeedback => {
  const { id } = useContext(FeedbackContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const { requestContext } = useCTW();
  const telemetry = useTelemetry();

  const submit = useMemo(
    () => async (data: FeedbackForm) => {
      setIsSubmitting(true);
      setIsError(false);
      setErrorMessage(undefined);

      try {
        const body = JSON.stringify({
          query_id: id,
          feedback: data.feedback,
          rating: data.feedbackType,
          target: `${resource.resourceType}/${resource.id}`,
        });
        await fetch(
          `${getApiServiceBaseUrl({ service: 'search-poc', env: requestContext.env })}/feedback`,
          {
            method: 'POST',
            body,
            headers: {
              Authorization: `Bearer ${requestContext.authToken}`,
              'Content-Type': 'application/json',
              'Zus-Account': requestContext.builderId,
            },
          },
        );
        // Form has been submitted to the search service
        setIsSubmitted(true);
      } catch (e) {
        const err = e as Error;
        setErrorMessage(err.message);
        setIsError(true);
      } finally {
        setIsSubmitting(false);
        telemetry.trackInteraction('feedback_submit', {
          feedbackType: data.feedbackType,
          feedbackResourceType: resource.resourceType,
          feedbackResourceId: resource.id,
        });
      }
    },
    [
      id,
      resource,
      requestContext.authToken,
      requestContext.builderId,
      requestContext.env,
      telemetry,
    ],
  );

  return {
    submit,
    isSubmitting,
    isSubmitted,
    isError,
    errorMessage,
  };
};
